// @flow

import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';

import checkmark from '../../../assets/svg/icons/checkmark.svg';

import UnstyledButton from './UnstyledButton';

const CHECKBOX_SIZE = '18px';

const CheckboxBorder = styled.div`
  width: ${CHECKBOX_SIZE};
  height: ${CHECKBOX_SIZE};
  border: 2px solid ${props => props.theme.avatarBorderColor};
  border-radius: 2px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: visible;
`;

const CheckboxInnerContent = styled.div`
  background-color: ${props => props.theme.brand};
  border-radius: 2px;
  box-sizing: border-box;
  width: ${CHECKBOX_SIZE};
  height: ${CHECKBOX_SIZE};
  min-width: ${CHECKBOX_SIZE};
  min-height: ${CHECKBOX_SIZE};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CheckmarkIcon = styled(animated.img)`
  min-width: ${CHECKBOX_SIZE};
  min-height: ${CHECKBOX_SIZE};
  height: ${CHECKBOX_SIZE};
  width: ${CHECKBOX_SIZE};
  max-height: ${CHECKBOX_SIZE};
  max-width: ${CHECKBOX_SIZE};
`;

const SPRING_ANIMATION_CONFIG = {
  mass: 1,
  tension: 220,
  friction: 14
};

type Props = {
  initialValue: boolean,
  onToggle: (isSelected: boolean) => void
};

export function CheckboxComponent({ initialValue, onToggle }: Props, ref: any) {
  const [isSelected, setIsSelected] = useState(initialValue);
  const springAnimation = useRef({
    transform: `scale(${initialValue ? 1 : 0})`,
    config: SPRING_ANIMATION_CONFIG
  }).current;

  const [checkmarkBgAnimationProps, setCheckmarkBgAnimation] = useSpring(() => springAnimation);
  const [checkmarkAnimationProps, setCheckmarkAnimation] = useSpring(() => ({
    ...springAnimation,
    delay: 250
  }));

  const onClick = () => {
    const toggled = !isSelected;
    setIsSelected(toggled);
    onToggle(toggled);

    const config = getConfig(toggled);
    const updatedAnimation = {
      transform: `scale(${toggled ? 1 : 0})`,
      config
    };

    setCheckmarkBgAnimation(updatedAnimation);
    setCheckmarkAnimation({
      ...updatedAnimation,
      delay: 250
    });
  };

  const getConfig = (selected: boolean) => (selected ? SPRING_ANIMATION_CONFIG : { duration: 250 });

  useImperativeHandle(ref, () => ({ onClick }));

  return (
    <UnstyledButton onClick={onClick}>
      <CheckboxBorder>
        <animated.div style={checkmarkBgAnimationProps}>
          <CheckboxInnerContent>
            <CheckmarkIcon style={checkmarkAnimationProps} src={checkmark} />
          </CheckboxInnerContent>
        </animated.div>
      </CheckboxBorder>
    </UnstyledButton>
  );
}

// $FlowFixMe
export default forwardRef(CheckboxComponent);
